'use client'

import { useState } from 'react'

import dynamic from 'next/dynamic'

import type { ImageSizingProps } from '@/components/image/types'

import { YoutubeThumbnail } from './youtube-thumbnail'

// The article's thumbnail stays server-rendered. The dialog, image gallery,
// and player code are only needed once someone chooses to watch a video.
const Gallery = dynamic(() => import('@/components/client/shared/gallery').then((module) => module.Gallery), {
  ssr: false,
})

export interface YoutubeClientProps {
  videoId: string
  title?: string
  thumbnail?: string
  caption?: string
  eager?: boolean
  delivery?: 'same-origin'
  sizing?: ImageSizingProps
  editableProps?: Record<string, unknown>
}

const PlayButton = () => (
  <span className="absolute-center flex h-[62px] w-[62px] items-center justify-center md:h-[80px] md:w-[80px] lg:h-[86px] lg:w-[86px]">
    <svg viewBox="0 0 68 48" className="h-full w-full">
      <path
        fill="red"
        d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26"
      />
      <path fill="#fff" d="M45 24 27 14v20" />
    </svg>
  </span>
)

export const YoutubeClient = ({
  videoId,
  title,
  thumbnail,
  caption,
  editableProps,
  eager = false,
  delivery,
  sizing,
}: YoutubeClientProps) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const videoTitle = title || 'YouTube video'

  return (
    <section
      className="n4-youtube not-prose laptop:max-w-[810px] w-full self-center justify-self-center max-lg:mx-0 max-lg:p-0 lg:px-0 first:m-0 first:max-w-full first:pt-0"
      {...editableProps}
    >
      <div className="video w-full shrink-0 [&_a_img]:max-w-full [&_a_img]:min-w-full">
        <div data-video-id={videoId} className="video-link relative mb-[20px] w-full">
          <a
            data-video-id={videoId}
            data-event-category="video click"
            data-event-label={`video - ${videoTitle}`}
            className="youtube_videobox_link block w-full"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setIsGalleryOpen(true)
            }}
          >
            <div
              className="bg-navy-10 relative w-full overflow-hidden rounded-3xl"
              style={{ aspectRatio: '1.77778 / 1', maxWidth: '100%' }}
            >
              <YoutubeThumbnail
                videoId={videoId}
                title={videoTitle}
                thumbnail={thumbnail}
                eager={eager}
                delivery={delivery}
                sizing={sizing}
              />
            </div>
            <PlayButton />
          </a>
        </div>
      </div>
      {caption && (
        <div className="captions-bottom border-navy mb-0 flex h-fit min-h-5.25 w-full items-start justify-start border-l-2 pl-2">
          <span className="text-md text-navy max-xs:text-[1.15rem] max-xs:font-[350] flex-1 leading-normal font-light not-italic tracking-[-0.015rem]">
            {caption}
          </span>
        </div>
      )}
      {isGalleryOpen && (
        <Gallery media={[{ type: 'youtube', videoId, title }]} isOpen onClose={() => setIsGalleryOpen(false)} />
      )}
    </section>
  )
}
